import { Box, Button, InputAdornment, TextField, Typography, CircularProgress } from '@mui/material'
import React, { useState, useRef, useEffect } from 'react'
import { Logo } from '../assets/images'
import Grid from '@mui/material/Grid2'
import Link from '@mui/material/Link';
import 'react-international-phone/style.css';
import { useNavigate, useSearchParams } from 'react-router-dom'
import chargebeeService from '../services/chargebee.service'
import { CardComponent } from '@chargebee/chargebee-js-react-wrapper';
import { decodeIdToken, decodeMetadataToken } from '../utils/token.utils';
import { toast } from 'react-toastify';
import { LeadsquaredService } from '../services/leadsquared/leadsquared.service';

const SSOSignup = () => {
    const navigate = useNavigate();
    const [showPromoField, setShowPromoField] = useState(false);
    const [value, setValue] = React.useState(0);

    const [formData, setFormData] = useState({
        fullName: '',
        email: ''
    });
    const [formErrors, setFormErrors] = useState({
        fullName: '',
        email: ''
    });
    const [isLoading, setIsLoading] = useState(false);

    const [cardToken, setCardToken] = useState<string>("");
    const [cardError, setCardError] = useState<string | null>(null);
    const cardRef = useRef<any>(null);

    const [searchParams] = useSearchParams();
    const [planDetails, setPlanDetails] = useState<any>(null);
    const [isLoadingPlan, setIsLoadingPlan] = useState(false);

    const [promoCode, setPromoCode] = useState('');
    const [appliedPromo, setAppliedPromo] = useState<{
        code: string;
        discount: number;
        type: string;
    } | null>(null);
    const [promoError, setPromoError] = useState('');

    const [isChargebeeReady, setIsChargebeeReady] = useState(false);

    const planId = searchParams.get('planId');
    const cleanPlanId = planId?.replace(/-\d+$/, '');

    // Add state for billing period
    const [billingPeriod, setBillingPeriod] = useState('yearly');

    useEffect(() => {
        const fetchPlanDetails = async () => {
            if (!cleanPlanId) return;

            setIsLoadingPlan(true);
            try {
                const response = await chargebeeService.getPlanById(cleanPlanId);
                setPlanDetails(response.data);
                
                // Set billing period based on plan details
                if (response.data && response.data.periodUnit) {
                    setBillingPeriod(response.data.periodUnit === 'year' ? 'yearly' : 'monthly');
                }
            } catch (error) {
                console.error('Failed to fetch plan details:', error);
            } finally {
                setIsLoadingPlan(false);
            }
        };

        fetchPlanDetails();
    }, [cleanPlanId]);

    useEffect(() => {
        const checkChargebee = () => {
            if (window.Chargebee) {
                setIsChargebeeReady(true);
            } else {
                setTimeout(checkChargebee, 100);
            }
        };

        checkChargebee();
    }, []);

    useEffect(() => {
        const decodedToken = decodeIdToken();
        if (decodedToken) {
            setFormData({
                fullName: decodedToken.name ||
                    `${decodedToken.given_name || ''} ${decodedToken.family_name || ''}`.trim(),
                email: decodedToken.email || ''
            });
        }
    }, []);

    // Add useEffect to handle billing period changes
    useEffect(() => {
        // Only proceed if we already have plan details
        if (!planDetails || !planId) return;

        // Get the base plan ID without quantity suffix
        const basePlanId = cleanPlanId || '';

        // Get the quantity part if it exists
        const quantitySuffix = planId.match(/-(\d+)$/)?.[0] || '';

        // Determine the new plan ID based on billing period
        let newPlanId: string;
        if (planDetails.periodUnit === 'year' && billingPeriod === 'monthly') {
            // Switch from yearly to monthly
            const monthlyPlanId = basePlanId.replace(/-yearly|-year/i, '-monthly');
            newPlanId = monthlyPlanId + quantitySuffix;
        } else if (planDetails.periodUnit === 'month' && billingPeriod === 'yearly') {
            // Switch from monthly to yearly
            const yearlyPlanId = basePlanId.replace(/-monthly|-month/i, '-yearly');
            newPlanId = yearlyPlanId + quantitySuffix;
        } else {
            // No change needed
            return;
        }

        // Update URL with new plan ID without page reload
        const params = new URLSearchParams(window.location.search);
        params.set('planId', newPlanId);
        const newUrl = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState({}, '', newUrl);

        // Refetch plan details with the new plan ID
        const fetchNewPlanDetails = async () => {
            setIsLoadingPlan(true);
            try {
                const newCleanPlanId = newPlanId.replace(/-\d+$/, '');
                const response = await chargebeeService.getPlanById(newCleanPlanId);
                setPlanDetails(response.data);
            } catch (error) {
                console.error('Failed to fetch new plan details:', error);
            } finally {
                setIsLoadingPlan(false);
            }
        };

        fetchNewPlanDetails();
    }, [billingPeriod]);

    useEffect(() => {
        const checkChargebee = () => {
            if (window.Chargebee) {
                setIsChargebeeReady(true);
            } else {
                setTimeout(checkChargebee, 100);
            }
        };

        checkChargebee();
    }, []);

    useEffect(() => {
        const decodedToken = decodeIdToken();
        if (decodedToken) {
            setFormData({
                fullName: decodedToken.name ||
                    `${decodedToken.given_name || ''} ${decodedToken.family_name || ''}`.trim(),
                email: decodedToken.email || ''
            });
        }
    }, []);

    const getPlanPrice = () => {
        if (isLoadingPlan) return { original: 'Loading...', discounted: 'Loading...' };
        if (!planDetails) return { original: '$00.00/month', discounted: '$00.00/month' };

        const quantity = planId ? parseInt(planId.match(/-(\d+)$/)?.[1] || '0') : 0;

        const tier = planDetails.tiers.find((tier: any) => {
            if (!tier.ending_unit) {
                return quantity >= tier.starting_unit;
            }
            return quantity >= tier.starting_unit && quantity <= tier.ending_unit;
        });

        if (!tier) return { original: '$00.00/month', discounted: '$00.00/month' };

        const originalPrice = tier.price / 100; // Convert cents to dollars
        let discountedPrice = originalPrice;

        // Apply discount if promo code is applied
        if (appliedPromo) {
            if (appliedPromo.type === 'percentage') {
                discountedPrice = originalPrice * (1 - appliedPromo.discount / 100);
            } else if (appliedPromo.type === 'fixed_amount') {
                discountedPrice = Math.max(0, originalPrice - (appliedPromo.discount / 100));
            }
        }

        // Adjust price based on billing period
        const period = planDetails.periodUnit;
        const displayOriginal = planDetails.periodUnit === 'year' ? originalPrice : originalPrice;
        const displayDiscounted = planDetails.periodUnit === 'year' ? discountedPrice  : discountedPrice;

        return {
            original: `$${Math.round(displayOriginal)}/${period}`,
            discounted: `$${Math.round(displayDiscounted)}/${period}`,
            hasDiscount: appliedPromo !== null && displayDiscounted < displayOriginal
        };
    };


    const validateForm = () => {
        const errors = {
            fullName: '',
            email: ''
        };
        let isValid = true;

        // Check required fields
        if (!formData.fullName.trim()) {
            errors.fullName = 'Full name is required';
            isValid = false;
        }

        if (!formData.email.trim()) {
            errors.email = 'Email is required';
            isValid = false;
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            errors.email = 'Please enter a valid email';
            isValid = false;
        }

        setFormErrors(errors);
        return isValid;
    };


    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        // Simply update the form data with the raw input value
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error when user starts typing
        setFormErrors(prev => ({
            ...prev,
            [name]: ''
        }));
    };

    const updateLeadsquaredActivity = async (email: string | undefined, planId: string | undefined) => {
        if (!email || !planId) {
            console.error('Email or planId is missing in LS');
            return;
        }
        
        setIsLoading(true);
        try {
            const prospectId = await LeadsquaredService.getLeadsByEmail(email as string);
            await LeadsquaredService.joinActivity({
                id: prospectId.data,
                activityEvent: 205,
                activityValue: planId,
                activityNote: "SSO Authentication"
            });
            await LeadsquaredService.joinActivity({
                id: prospectId.data,
                activityEvent: 205,
                activityValue: "Currently In Trial",
                activityNote: "SSO Authentication"
            });
            const leadData = {
                "leadId": prospectId.data,
                "fields": [
                    { "Attribute": "mx_Plan_Name", "Value": planId },
                    { "Attribute": "mx_Status", "Value": "Active" }
                ]
            }
            await LeadsquaredService.updateLsField(leadData);
        } catch (error) {
            console.error('Error in LS tags:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleNext = async () => {
        if (!validateForm()) {
            return;
        }

        setIsLoading(true);
        try {
            // Tokenize the card
            if (!cardRef.current) {
                setCardError('Card component not initialized');
                return;
            }

            const result = await cardRef.current.tokenize();

            if (result.error) {
                setCardError(result.error.message || 'Card tokenization failed');
                return;
            }

            const cardTokenResult = result.token;
            setCardToken(cardTokenResult);
            setCardError(null);

            if (!cardTokenResult) {
                setCardError('Please enter valid card details');
                toast.error('Please enter valid card details');
                return;
            }

            const metadata = decodeMetadataToken();
            const customerId = metadata?.companies?.[0]?.id || ''
            const basePlanId = planDetails?.id
            const cardToken = cardTokenResult
            const couponId = appliedPromo?.code
            const planUnits = parseInt(planId?.match(/-(\d+)$/)?.[1] || '1') || 1

            // Call both APIs in parallel for better performance
            const [subscriptionResponse, cardResponse] = await Promise.all([
                chargebeeService.createSubscription(customerId, basePlanId, couponId, planUnits),
                chargebeeService.addPaymentSource(customerId, cardToken)
            ]);

            // Update Leadsquared activity
            await updateLeadsquaredActivity(formData.email, cleanPlanId);
            
            console.log('Subscription created successfully:', subscriptionResponse, cardResponse);
            toast.success('Subscription created successfully');
            
            navigate(`/loading-dashboard?ew_email=${formData.email}&planId=${planId}&sso=true`);
        } catch (error: any) {
            toast.error('Failed to start trial');
            console.error('Failed to start trial:', error);
            if (error.name === 'INVALID_CARD') {
                setCardError('Please enter valid card details');
            } else {
                toast.error(error.message || 'An error occurred. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const validateCoupon = async () => {
        if (!promoCode.trim()) {
            setPromoError('Please enter a promo code');
            return;
        }

        try {
            const planId = searchParams.get('planId');
            const response = await chargebeeService.validateCoupon(
                promoCode,
                planId ? planId.replace(/-\d+$/, '') : undefined // Only pass planId if it exists
            );

            if (response.data.valid) {
                const { coupon } = response.data;
                setAppliedPromo({
                    code: coupon.id,
                    discount: coupon.discountPercentage || coupon.discountAmount,
                    type: coupon.discountType // 'percentage' or 'fixed_amount'
                });
                setPromoError('');
                setShowPromoField(false);
            } else {
                setPromoError(response.data.error || 'Invalid promo code');
                setAppliedPromo(null);
            }
        } catch (error) {
            console.error('Error validating coupon:', error);
            setPromoError('Failed to validate promo code');
            setAppliedPromo(null);
        }
    };

    return (
        <>
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={{ xs: "0", sm: "unset" }} p={{ xs: "16px 16px 11px", sm: "18px 20px 16px", md: "24px 20px 16px" }} bgcolor="#f5f6f8" position="sticky" top="0" zIndex="2">
                <Box><img src={Logo} alt="logo" /></Box>
                <Box display="flex" alignItems="center">
                    <Button
                        variant="text"
                        onClick={() => navigate('/logout')}
                        sx={{
                            textTransform: "capitalize", color: "#22B060 !important", fontSize: "14px", display: "flex", gap: "8px", fontWeight: "500", p: "4px", minWidth: "50px",
                            "&:hover": {
                                boxShadow: "none!important",
                                borderRadius: "8px",
                                "& svg": {
                                    transform: "translateX(2px)",
                                    transition: "all 0.3s ease",
                                }
                            }
                        }}>
                        Logout
                    </Button>
                </Box>

            </Box>
            <Box bgcolor="#F5F6F8">
                <Grid container rowSpacing={{ xs: 3, sm: 3, md: 3 }} columnSpacing={{ xs: 3, sm: 2, md: 3 }} mb="24px" bgcolor="#F5F6F8" maxWidth="1400px" margin="auto">
                    <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                        <Box p={{ xs: "16px", sm: "20px", xl: "48px" }} mt="38px" sx={{ background: "#F5F6F8", backgroundRepeat: "no-repeat", backgroundSize: "cover", backgroundPosition: "center" }}>
                            <Typography
                                color="#333333"
                                fontWeight="500"
                                fontSize={{ xs: "min(calc(18px + 6 * (100vw - 400px) / (1920 - 400)), 28px)", lg: "28px" }}
                                mb="8px"
                                textAlign="center"
                            >
                                Start Your Free Trial
                            </Typography>
                            <Box display="flex" gap="12px" mb="24px" flexWrap="wrap" justifyContent="center" alignItems="center">
                                <Typography
                                    color="#666666"
                                    variant='body1'
                                    textAlign="center"
                                >
                                    After 14-Day trial: {appliedPromo && getPlanPrice().hasDiscount ? (
                                        <>
                                            <Typography component="span" variant="body1" sx={{ textDecoration: 'line-through', color: '#666666', mr: 1 }}>
                                                {getPlanPrice().original}
                                            </Typography>
                                            <Typography component="span" variant="body1" color="#22B060" fontWeight="600">
                                                {getPlanPrice().discounted}
                                            </Typography>
                                        </>
                                    ) : (
                                        <Typography component="span" variant="body1" color="#22B060" fontWeight="600">
                                            {getPlanPrice().discounted}
                                        </Typography>
                                    )} applicable
                                </Typography>
                            </Box>
                            <Box>
                                <div className="container toggle-container">
                                    <div className="switch-wrapper monthly-checkbox">
                                        <input
                                            type="radio"
                                            name="plan-switch"
                                            value="monthly"
                                            className="plan-switch button_first"
                                            id="monthlyside"
                                            checked={billingPeriod === 'monthly'}
                                            onChange={(e) => setBillingPeriod(e.target.value)}
                                        />
                                        <input
                                            type="radio"
                                            name="plan-switch"
                                            value="yearly"
                                            className="plan-switch button_first"
                                            id="yearlyside"
                                            checked={billingPeriod === 'yearly'}
                                            onChange={(e) => setBillingPeriod(e.target.value)}
                                        />
                                        <label htmlFor="yearlyside">Yearly</label>
                                        <label htmlFor="monthlyside">Monthly</label>
                                        <span className="highlighter"></span>
                                    </div>
                                </div>
                            </Box>

                            <Box p={{ xs: "24px 20px", sm: "42px 28px" }} display="flex" flexDirection="column" justifyContent="center" alignItems="center" boxShadow="0px 4px 8px 0px rgba(0, 0, 0, 0.05)" border="1px solid #E9E9E9" bgcolor="#FFFFFF" maxWidth="574px" margin="auto" borderRadius="24px" marginTop="16px" zIndex={{ lg: '3' }} position="relative">


                                <Box sx={{ width: '100%' }} className="umbrella-signIn-tabs">
                                    <Grid container rowSpacing={{ xs: 3, sm: 3, md: 3 }} columnSpacing={{ xs: 3, sm: 2, md: 3 }} mb="24px">
                                        <Grid size={{ xs: 12 }}>
                                            <TextField
                                                variant="outlined"
                                                label="Full name"
                                                name="firstName"
                                                value={formData.fullName}
                                                onChange={handleInputChange}
                                                error={!!formErrors.fullName}
                                                helperText={formErrors.fullName}
                                                InputProps={{
                                                    readOnly: true,
                                                }}
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: '3px',
                                                        borderColor: '#666666',
                                                    },

                                                    " .MuiInputLabel-root.Mui-focused": {
                                                        color: "inherit",
                                                    },

                                                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                        borderColor: '#333333',
                                                        border: '1px solid'
                                                    },

                                                    "& .MuiOutlinedInput-input": {
                                                        padding: '14px 16px',
                                                        background: '#ffffff',
                                                        borderRadius: '8px'
                                                    },
                                                    "&.MuiInputBase-input::placeholder": {
                                                        color: '#333333',
                                                        fontSize: '16px',
                                                        opacity: '0.8'
                                                    },
                                                    "& .MuiOutlinedInput-root.Mui-readOnly": {
                                                        backgroundColor: '#f5f5f5',
                                                    },
                                                }}
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12 }}>
                                            <TextField
                                                variant="outlined"
                                                label="Work email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                error={!!formErrors.email}
                                                helperText={formErrors.email}
                                                InputProps={{
                                                    readOnly: true,
                                                }}
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: '3px',
                                                        borderColor: '#666666',
                                                    },

                                                    " .MuiInputLabel-root.Mui-focused": {
                                                        color: "inherit",
                                                    },

                                                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                        borderColor: '#333333',
                                                        border: '1px solid'
                                                    },

                                                    "& .MuiOutlinedInput-input": {
                                                        padding: '14px 16px',
                                                        background: '#ffffff',
                                                        borderRadius: '8px'
                                                    },
                                                    "&.MuiInputBase-input::placeholder": {
                                                        color: '#333333',
                                                        fontSize: '16px',
                                                        opacity: '0.8'
                                                    },
                                                    "& .MuiOutlinedInput-root.Mui-readOnly": {
                                                        backgroundColor: '#f5f5f5',
                                                    },
                                                }}
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12 }}>
                                            <Box display="flex" justifyContent="end" flexDirection="column">
                                                {!showPromoField ? (
                                                    <>
                                                        {appliedPromo ? (
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '8px',
                                                                    padding: '4px 8px',
                                                                    backgroundColor: '#F0FFF4',
                                                                    borderRadius: '4px',
                                                                    width: 'fit-content',
                                                                    marginLeft: 'auto'
                                                                }}
                                                            >
                                                                <Typography color="#1D9652" fontSize="14px">
                                                                    {appliedPromo?.code} applied
                                                                </Typography>
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    width="16"
                                                                    height="16"
                                                                    viewBox="0 0 16 16"
                                                                    fill="none"
                                                                    style={{ cursor: 'pointer' }}
                                                                    onClick={() => {
                                                                        setAppliedPromo(null);
                                                                        setPromoCode('');
                                                                    }}
                                                                >
                                                                    <path d="M8 1C4.1 1 1 4.1 1 8C1 11.9 4.1 15 8 15C11.9 15 15 11.9 15 8C15 4.1 11.9 1 8 1ZM10.7 11.5L8 8.8L5.3 11.5L4.5 10.7L7.2 8L4.5 5.3L5.3 4.5L8 7.2L10.7 4.5L11.5 5.3L8.8 8L11.5 10.7L10.7 11.5Z" fill="#1D9652" />
                                                                </svg>
                                                            </Box>
                                                        ) : (
                                                            <Typography
                                                                color="#667085"
                                                                fontSize="16px"
                                                                display="flex"
                                                                justifyContent="end"
                                                                sx={{
                                                                    cursor: 'pointer',
                                                                    '&:hover': {
                                                                        textDecoration: 'underline'
                                                                    }
                                                                }}
                                                                onClick={() => setShowPromoField(true)}
                                                            >
                                                                Have a promo code?
                                                            </Typography>
                                                        )}
                                                    </>
                                                ) : (
                                                    <TextField
                                                        variant="outlined"
                                                        placeholder="Promo Code:"
                                                        value={promoCode}
                                                        onChange={(e) => {
                                                            setPromoCode(e.target.value);
                                                            setPromoError('');
                                                        }}
                                                        error={!!promoError}
                                                        helperText={promoError}
                                                        sx={{
                                                            width: "100%",
                                                            "& .MuiOutlinedInput-root": {
                                                                borderRadius: '3px',
                                                                borderColor: '#C7C7C7',
                                                                background: '#ffffff',
                                                                paddingRight: '0'
                                                            },
                                                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                                borderColor: '#333333',
                                                                border: '1px solid'
                                                            },
                                                            "& .MuiOutlinedInput-input": {
                                                                padding: '14px 16px',
                                                                background: '#ffffff',
                                                                borderRadius: '8px'
                                                            },
                                                            "&.MuiInputBase-input::placeholder": {
                                                                color: '#333333',
                                                                fontSize: '16px',
                                                                opacity: '0.8'
                                                            },
                                                        }}
                                                        slotProps={{
                                                            input: {
                                                                endAdornment: (
                                                                    <InputAdornment
                                                                        position="end"
                                                                        sx={{
                                                                            ".MuiTypography-root": {
                                                                                color: "#333333",
                                                                                fontWeight: "500",
                                                                                fontSize: "16px",
                                                                            },
                                                                            paddingRight: "10px",
                                                                        }}
                                                                    >
                                                                        <Box display="flex" justifyContent="center" alignItems="center">
                                                                            <Box
                                                                                sx={{
                                                                                    background: '#22B060',
                                                                                    borderRadius: '4px',
                                                                                    padding: '4px 10px',
                                                                                    marginRight: '4px',
                                                                                    minWidth: '57px',
                                                                                    fontSize: '14px',
                                                                                    fontWeight: '500',
                                                                                    color: '#fff',
                                                                                    cursor: 'pointer',
                                                                                    '&:hover': {
                                                                                        background: '#1a9550',
                                                                                        transition: 'background-color 0.3s ease'
                                                                                    }
                                                                                }}
                                                                                onClick={validateCoupon}
                                                                            >
                                                                                Apply
                                                                            </Box>
                                                                            <svg
                                                                                xmlns="http://www.w3.org/2000/svg"
                                                                                width="17"
                                                                                height="16"
                                                                                viewBox="0 0 17 16"
                                                                                fill="none"
                                                                                style={{ cursor: 'pointer' }}
                                                                                onClick={() => {
                                                                                    setShowPromoField(false);
                                                                                    setPromoError('');
                                                                                }}
                                                                            >
                                                                                <path d="M8.07324 1C4.17324 1 1.07324 4.1 1.07324 8C1.07324 11.9 4.17324 15 8.07324 15C11.9732 15 15.0732 11.9 15.0732 8C15.0732 4.1 11.9732 1 8.07324 1ZM10.7732 11.5L8.07324 8.8L5.37324 11.5L4.57324 10.7L7.27324 8L4.57324 5.3L5.37324 4.5L8.07324 7.2L10.7732 4.5L11.5732 5.3L8.87324 8L11.5732 10.7L10.7732 11.5Z" fill="#A3A3A3" />
                                                                            </svg>
                                                                        </Box>
                                                                    </InputAdornment>
                                                                ),
                                                            },
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        </Grid>
                                        <Grid size={{ xs: 12 }}>
                                            <Box width="100%" mb="24px" sx={{
                                                "& .CbHosted": {
                                                    borderRadius: '3px',
                                                    border: '1px solid rgba(0, 0, 0, 0.23)',
                                                    padding: '14px 16px',
                                                    "&:hover, &--focus": {
                                                        borderColor: '#666666',
                                                    }
                                                }
                                            }}>
                                                {isChargebeeReady ? (
                                                    <CardComponent
                                                        ref={cardRef}
                                                        currency="USD"
                                                        showTestCards={true}
                                                        styles={{
                                                            border: "1px solid #666666",
                                                            base: {
                                                                padding: "14px 16px",
                                                                border: "1px solid #666666",
                                                                fontSize: '16px',
                                                                fontFamily: 'Inter, sans-serif',
                                                                color: '#333333',
                                                                '::placeholder': {
                                                                    color: '#667085',
                                                                },
                                                            },
                                                            invalid: {
                                                                color: '#ff0000',
                                                            },
                                                        }}
                                                        onChange={(status: any) => {
                                                            if (status.error) {
                                                                setCardError(status.error.message);
                                                            } else {
                                                                setCardError(null);
                                                            }
                                                        }}
                                                        onReady={() => {
                                                            console.log('Card component ready');
                                                        }}
                                                        onBlur={() => {
                                                            console.log('Card component blurred');
                                                        }}
                                                        onFocus={() => {
                                                            console.log('Card component focused');
                                                        }}
                                                    />
                                                ) : (
                                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                                        <CircularProgress />
                                                    </Box>
                                                )}
                                                {cardError && (
                                                    <Typography color="error" fontSize="12px" mt={1}>
                                                        {cardError}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Grid>
                                    </Grid>
                                    <Button
                                        variant="contained"
                                        disableElevation
                                        disabled={isLoading}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            textTransform: "unset",
                                            height: "51px",
                                            width: "100%",
                                            gap: "8px",
                                            fontSize: "16px",
                                            fontWeight: "500",
                                            margin: "auto",
                                            bgcolor: "#0382F7 !important",
                                            "&:hover": { bgcolor: "#006dd1 !important" },
                                            "&.Mui-disabled": {
                                                bgcolor: "#178EFC !important",
                                                opacity: 0.7
                                            }
                                        }}
                                        onClick={handleNext}
                                    >
                                        {isLoading ? (
                                            <CircularProgress size={24} sx={{ color: 'white' }} />
                                        ) : (
                                            'Start Free Trial'
                                        )}
                                    </Button>
                                    <Typography variant="body2" color="#667085" mt="8px" textAlign="center" >By proceeding, you agree to our <Typography component="a" href="#" variant="inherit" color="#22B060" sx={{
                                        textDecoration: "none", '&:hover': {
                                            textDecoration: 'underline'
                                        }
                                    }}>Terms and services</Typography></Typography>

                                    <Box className="login-authenticator-wrapper" sx={{ position: "unset!important" }}>
                                        <Box
                                            className="ew-copyright"
                                            sx={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: { xs: "20px" },
                                                justifyContent: { xs: "center" },
                                                alignItems: 'center',
                                                padding: '0 !important',
                                                position: 'unset!important',
                                                margin: '36px 0 0 !important',
                                            }}
                                        >
                                            <Typography
                                                component="span"
                                                fontSize="12px !important"
                                                color='#667085 !important'
                                                borderRight={{ xs: "none !important", sm: "1px solid #c5c5c5 !important" }}
                                            >
                                                &copy; 2025 All Rights Reserved
                                            </Typography>
                                            <Box
                                                component="ul"
                                                sx={{
                                                    display: 'flex',
                                                    listStyle: 'none',
                                                    margin: 0,
                                                    padding: 0,
                                                    gap: '4px',
                                                    position: 'relative',
                                                    "li::after": {
                                                        background: "#667085 !important",
                                                    }
                                                }}
                                                flexWrap="wrap"
                                                justifyContent="center"
                                            >
                                                <Box
                                                    component="li"
                                                    paddingLeft="0!important"
                                                >
                                                    <Link
                                                        href="https://easywebinar.com/terms-conditions/"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className='terms'
                                                        sx={{
                                                            textDecoration: 'none',
                                                            color: '#667085 !important',
                                                            fontSize: '12px !important',
                                                            marginRight: "4px",

                                                            '&:hover': {
                                                                textDecoration: 'underline',
                                                            },

                                                        }}
                                                    >
                                                        Terms of Service
                                                    </Link>
                                                </Box>
                                                <Box
                                                    component="li"
                                                >
                                                    <Link
                                                        href="https://easywebinar.com/privacy/"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        sx={{
                                                            textDecoration: 'none',
                                                            color: '#667085 !important',
                                                            fontSize: '12px !important',
                                                            marginRight: "4px",
                                                            '&:hover': {
                                                                textDecoration: 'underline',
                                                            },
                                                        }}
                                                    >
                                                        Privacy Policy
                                                    </Link>
                                                </Box>
                                                <Box
                                                    component="li"
                                                >
                                                    <Link
                                                        href="https://easywebinar.com/cookies/"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        sx={{
                                                            textDecoration: 'none',
                                                            color: '#667085 !important',
                                                            fontSize: '12px !important',
                                                            '&:hover': {
                                                                textDecoration: 'underline',
                                                            },
                                                        }}
                                                    >
                                                        Security
                                                    </Link>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box >
                        </Box >
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 6 }} p={{ xs: "16px", sm: "20px", xl: "48px" }} bgcolor="#F5F6F8">
                        <Box borderRadius="20px" bgcolor="#FFFFFF" p={{ xs: "24px 0 20px 20px", md: "42px 0 32px 32px" }}>
                            <Box pr={{ xs: "16px", sm: "20px", md: "24px" }}>
                                <Typography
                                    variant="h3"
                                    color="#161515"
                                    fontSize={{
                                        xs: "min(calc(24px + 12 * (100vw - 400px) / (1920 - 400)), 36px)",
                                    }}
                                    fontWeight="600"
                                >
                                    <Typography
                                        component="span"
                                        variant="inherit"
                                        color='#22B060'
                                    >
                                        Your Webinar
                                    </Typography> Your Way
                                </Typography>
                                <Typography
                                    variant="h5"
                                    color=" #161515"
                                    mt={{ xs: "24px", sm: "min(calc(40px + 8 * (100vw - 900px) / (1920 - 900)), 40px)" }}
                                    fontSize={{
                                        xs: "min(calc(16px + 6 * (100vw - 400px) / (1920 - 400)), 24px)",
                                    }}
                                >
                                    Seamlessly host <Typography component="span" variant="inherit" color='#22B060' fontWeight="500">Live</Typography>, <Typography component="span" variant="inherit" color='#22B060' fontWeight="500">Automated</Typography>, and <Typography component="span" variant="inherit" color='#22B060' fontWeight="500">Hybrid</Typography> webinars with powerful engagement tools. Easy to set up, designed to grow your audience.
                                </Typography>
                            </Box>
                            <Box sx={{ "& img": { width: "100%", height: "auto" } }} mt={{ xs: "24px", sm: "48px" }}>
                                <picture>
                                    <source srcSet="https://easywebinar-site.s3.us-east-1.amazonaws.com/images/autheticator-app-images/easywebinar-lg-with-img-logo-bg.webp" type="image/webp" />
                                    <img
                                        src="https://easywebinar-site.s3.us-east-1.amazonaws.com/images/autheticator-app-images/easywebinar-lg-with-img-logo-bg.png"
                                        alt="easywebinar with people"
                                    />
                                </picture>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Box>

        </>
    )
}
export default SSOSignup
